import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Tag,
  Spin,
  message,
  List,
  Space,
  Modal,
  Empty,
  Form,
  Input,
  Alert,
  Table,
} from 'antd';
import {
  CrownOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ArrowUpOutlined,
  PhoneOutlined,
  SendOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import './CustomerDashboard.css';

const { Title, Text } = Typography;

const STATUS_MAP = {
  pending: { color: '#f1a134', label: 'Pending' },
  provider_assigned: { color: '#2e9bd6', label: 'Assigned to Provider' },
  provider_submitted: { color: '#2e7eba', label: 'Under Review' },
  approved: { color: '#52b788', label: 'Approved' },
  rejected: { color: '#e5484d', label: 'Rejected' },
};

const formatNPR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const Membership = () => {
  const [loading, setLoading] = useState(true);
  const [membershipInfo, setMembershipInfo] = useState(null);
  const [requests, setRequests] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [requestModal, setRequestModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestForm] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, tiersRes] = await Promise.all([
        api.get('/membership/status'),
        api.get('/membership/tiers'),
      ]);
      setMembershipInfo(statusRes.data?.data?.membership || null);
      setRequests(statusRes.data?.data?.requests || []);
      setTiers(tiersRes.data?.data || []);
    } catch (error) {
      console.error('Error fetching membership:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmitRequest = async (values) => {
    setSubmitting(true);
    try {
      const response = await api.post('/membership/request', values);
      message.success(response.data?.message || 'Request submitted!');
      setRequestModal(false);
      requestForm.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const isActive = membershipInfo?.is_active_member;

  const tierColumns = [
    {
      title: 'Points Range',
      key: 'range',
      render: (_, r) => (r.max_points ? `${r.min_points} - ${r.max_points}` : `${r.min_points}+`),
    },
    {
      title: 'Monthly',
      key: 'monthly',
      render: (_, r) => formatNPR(r.monthly_price),
    },
    {
      title: 'Yearly Price',
      key: 'yearly',
      render: (_, r) => (
        <Text strong style={{ color: '#2d5a3d' }}>
          {formatNPR(r.yearly_price)}
        </Text>
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="customer-page">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card className="customer-card">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <Title level={3} style={{ margin: 0, color: '#2d5a3d' }}>
                  My Membership
                </Title>
                <Text type="secondary">Manage your membership and view status</Text>
              </div>
              {!requests.some((r) =>
                ['pending', 'provider_assigned', 'provider_submitted'].includes(r.status),
              ) && (
                <Button
                  type="primary"
                  className="customer-btn-primary"
                  icon={<SendOutlined />}
                  onClick={() => setRequestModal(true)}
                >
                  Request Membership
                </Button>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card className="customer-card">
            <StatisticItem
              icon={<CrownOutlined />}
              title="Status"
              value={isActive ? 'Active Member' : 'Not a Member'}
              color={isActive ? '#52b788' : '#9ca3af'}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="customer-card">
            <StatisticItem
              icon={<ClockCircleOutlined />}
              title="Valid Until"
              value={
                membershipInfo?.membership_end_date
                  ? new Date(membershipInfo.membership_end_date).toLocaleDateString()
                  : 'N/A'
              }
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="customer-card">
            <StatisticItem
              icon={<ArrowUpOutlined />}
              title="Days Remaining"
              value={
                membershipInfo?.days_remaining > 0 ? `${membershipInfo.days_remaining} days` : 'N/A'
              }
              color={membershipInfo?.days_remaining > 0 ? '#2e9bd6' : '#9ca3af'}
            />
          </Card>
        </Col>

        {isActive && (
          <Col span={24}>
            <Alert
              type="success"
              showIcon
              icon={<CrownOutlined />}
              message="You are an active member!"
              description="You receive 100% discount on maintenance & repair services and 5% discount on all other services."
            />
          </Col>
        )}

        <Col span={24}>
          <Card
            className="customer-card"
            title={
              <span style={{ color: '#2d5a3d', fontWeight: 600 }}>
                <DollarOutlined /> Annual Membership Pricing
              </span>
            }
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Your yearly price is determined by assessment points. Higher points = higher tier.
            </Text>
            <Table
              columns={tierColumns}
              dataSource={tiers}
              rowKey="min_points"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card
            className="customer-card"
            title={
              <span style={{ color: '#2d5a3d', fontWeight: 600 }}>
                <FileTextOutlined /> Membership Request History
              </span>
            }
          >
            {requests.length === 0 ? (
              <Empty description="No membership requests yet" />
            ) : (
              <List
                dataSource={requests}
                renderItem={(item) => {
                  const statusMeta = STATUS_MAP[item.status] || {
                    color: 'default',
                    label: item.status,
                  };
                  return (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Space>
                            <span>Request #{item.id}</span>
                            <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <p style={{ margin: 0 }}>
                              Submitted: {new Date(item.created_at).toLocaleDateString()}
                              {item.assessment_points > 0 && ` | Points: ${item.assessment_points}`}
                              {item.yearly_price > 0 &&
                                ` | Yearly Price: ${formatNPR(item.yearly_price)}`}
                            </p>
                            {item.referral_code && (
                              <p style={{ margin: 0 }}>Referral Code: {item.referral_code}</p>
                            )}
                            {item.rejection_reason && (
                              <p style={{ margin: 0, color: '#e5484d' }}>
                                Reason: {item.rejection_reason}
                              </p>
                            )}
                          </div>
                        }
                      />
                      <div>
                        {item.assigned_provider && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Provider: {item.assigned_provider.name}
                          </Text>
                        )}
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title="Request Membership"
        open={requestModal}
        onCancel={() => {
          setRequestModal(false);
          requestForm.resetFields();
        }}
        footer={null}
        width={520}
      >
        <Form form={requestForm} layout="vertical" onFinish={handleSubmitRequest}>
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[
              { required: true, message: 'Enter your phone' },
              { pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' },
            ]}
          >
            <Input
              type="tel"
              onKeyPress={(e) => e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()}
              prefix={<PhoneOutlined />}
              placeholder="e.g. 98XXXXXXXX"
            />
          </Form.Item>
          <Form.Item
            name="address"
            label="Property Address"
            rules={[{ required: true, message: 'Enter property address' }]}
          >
            <Input.TextArea rows={2} placeholder="Enter the address for assessment" />
          </Form.Item>
          <Form.Item
            name="referral_code"
            label="Referral Code (Optional)"
            tooltip="Enter a referral code from an existing member"
          >
            <Input prefix={<GiftOutlined />} placeholder="e.g. ABC12345" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              className="customer-btn-primary"
              htmlType="submit"
              block
              loading={submitting}
              icon={<SendOutlined />}
            >
              Submit Request
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const StatisticItem = ({ icon, title, value, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: 32, color: color || '#2e9bd6', marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 18, fontWeight: 600, color: color || '#2d5a3d' }}>{value || '-'}</div>
  </div>
);

export default Membership;
